export type AllocationType = 'job' | 'stock' | 'overhead' | 'equipment' | 'vehicle' | 'suspense';

export interface ExpenseAccountOption {
  account_code: string;
  name: string;
  account_type?: string | null;
  account_subtype?: string | null;
  allowed_allocation_types?: AllocationType[] | null;
  is_active?: boolean | null;
  /** QBO external reference ID — null means this account has no QBO mapping yet. */
  external_ref?: string | null;
  /** External system this account is mapped to (e.g. 'quickbooks'). */
  external_system?: string | null;
}

export const ACCOUNT_TYPE_ALLOCATION_MAP: Record<string, AllocationType[]> = {
  asset: ['overhead', 'suspense'],
  expense: ['overhead', 'suspense'],
  // Revenue-type accounts allow job allocation (billable path) but default to
  // overhead — QBO parity: customer/job is opt-in, never forced.
  revenue: ['overhead', 'job', 'suspense'],
  liability: ['overhead', 'suspense'],
  equity: ['overhead', 'suspense'],
};

export const ACCOUNT_SUBTYPE_ALLOCATION_MAP: Record<string, AllocationType[]> = {
  // Expense subtypes — only COGS allows job allocation.
  // operating_expense allows vehicle allocation for fuel, toll, maintenance, etc.
  cost_of_goods_sold: ['job', 'stock', 'overhead', 'suspense'],
  operating_expense: ['overhead', 'vehicle', 'suspense'],
  other_expense: ['overhead', 'vehicle', 'suspense'],
  tax_expense: ['overhead', 'suspense'],
  // Asset subtypes — inventory allows stock, fixed_asset allows equipment
  bank: ['overhead', 'suspense'],
  cash: ['overhead', 'suspense'],
  accounts_receivable: ['overhead', 'suspense'],
  inventory: ['stock', 'overhead', 'suspense'],
  fixed_asset: ['equipment', 'overhead', 'suspense'],
  other_asset: ['overhead', 'suspense'],
  // Liability subtypes
  accounts_payable: ['overhead', 'suspense'],
  credit_card: ['overhead', 'suspense'],
  other_liability: ['overhead', 'suspense'],
  long_term_liability: ['overhead', 'suspense'],
  // Equity subtypes
  retained_earnings: ['overhead', 'suspense'],
  other_equity: ['overhead', 'suspense'],
  // Revenue subtypes — sales allows job allocation but defaults to
  // overhead: QBO parity — a customer/job link is opt-in, never forced.
  // Choosing 'job' explicitly still requires customer → job (the line is
  // then billable to that job, like QBO's billable-expense flag).
  sales: ['overhead', 'job', 'suspense'],
  other_income: ['overhead', 'job', 'suspense'],
};

export const ALL_ALLOCATION_TYPES: AllocationType[] = ['job', 'stock', 'overhead', 'equipment', 'vehicle', 'suspense'];

export const ALL_ALLOCATION_OPTIONS = [
  { value: 'job', label: 'Job', description: 'Link to a specific job' },
  { value: 'stock', label: 'Stock / Inventory', description: 'Link to inventory item' },
  { value: 'overhead', label: 'Overhead', description: 'General overhead allocation' },
  { value: 'equipment', label: 'Equipment / Fixed Asset', description: 'Link to equipment asset' },
  { value: 'vehicle', label: 'Vehicle', description: 'Link to a specific vehicle' },
  { value: 'suspense', label: 'Suspense', description: 'Temporary suspense account' },
] as const;

export function getAllowedAllocationTypes(
  accounts: ExpenseAccountOption[],
  accountCode: string | null | undefined,
): AllocationType[] {
  if (!accountCode) return ALL_ALLOCATION_TYPES;
  const account = accounts.find(a => a.account_code === accountCode);
  if (!account) return ALL_ALLOCATION_TYPES;

  // 1. Per-account override (highest priority)
  if (account.allowed_allocation_types && account.allowed_allocation_types.length > 0) {
    return account.allowed_allocation_types;
  }

  // 2. Subtype-based defaults (case-insensitive)
  if (account.account_subtype) {
    const subtypeKey = account.account_subtype.toLowerCase();
    if (ACCOUNT_SUBTYPE_ALLOCATION_MAP[subtypeKey]) {
      return ACCOUNT_SUBTYPE_ALLOCATION_MAP[subtypeKey];
    }
  }

  // 3. Fall back to account_type map (case-insensitive)
  if (account.account_type) {
    const typeKey = account.account_type.toLowerCase();
    if (ACCOUNT_TYPE_ALLOCATION_MAP[typeKey]) {
      return ACCOUNT_TYPE_ALLOCATION_MAP[typeKey];
    }
  }

  return ALL_ALLOCATION_TYPES;
}

export function getDerivedAllocationType(
  accounts: ExpenseAccountOption[],
  accountCode: string | null | undefined,
): AllocationType {
  const allowed = getAllowedAllocationTypes(accounts, accountCode);
  return allowed[0] ?? 'overhead';
}

/**
 * Document-context-aware allocation type. When the source document carries
 * a vehicle link (fuel DIN captured against a vehicle) and the GL account
 * permits 'vehicle' allocation, 'vehicle' is preferred over the account's
 * first allowed type so the captured vehicle link survives GL application
 * (e.g. a fuel DIN on a "Travel & Accommodation" operating-expense GL
 * whose subtype default would otherwise be 'overhead', silently dropping
 * the vehicle).
 */
export function getPreferredAllocationType(
  accounts: ExpenseAccountOption[],
  accountCode: string | null | undefined,
  hasVehicleLink: boolean,
): AllocationType {
  const allowed = getAllowedAllocationTypes(accounts, accountCode);
  if (hasVehicleLink && allowed.includes('vehicle')) return 'vehicle';
  return allowed[0] ?? 'overhead';
}

export interface ExpenseAccountMappingCategory {
  key: string;
  label: string;
  description: string;
  aliases?: string[];
}

export type ExpenseAccountMappings = Record<string, string>;

export const EXPENSE_ACCOUNT_MAPPING_CATEGORIES: ExpenseAccountMappingCategory[] = [
  {
    key: 'fuel',
    label: 'Fuel',
    description: 'Till slips, diesel, petrol, and forecourt card purchases.',
  },
  {
    key: 'toll',
    label: 'Tolls',
    description: 'Road tolls, gantries, and e-tag travel charges.',
  },
  {
    key: 'parking',
    label: 'Parking',
    description: 'Parking tickets, bay fees, and access charges.',
  },
  {
    key: 'maintenance',
    label: 'Maintenance / servicing',
    description: 'Routine servicing, repairs, tyres, workshops, and vehicle upkeep.',
    aliases: ['servicing'],
  },
  {
    key: 'breakdown',
    label: 'Breakdown / roadside',
    description: 'Emergency repairs, roadside assistance, towing, and recovery costs.',
    aliases: ['roadside'],
  },
  {
    key: 'purchase',
    label: 'General purchases',
    description: 'Fallback for field purchases when no more specific purchase category is captured.',
  },
  {
    key: 'parts',
    label: 'Parts',
    description: 'Spares, replacement components, and stocked parts.',
  },
  {
    key: 'supplies',
    label: 'Supplies',
    description: 'Office supplies, site supplies, and general business consumables.',
  },
  {
    key: 'equipment',
    label: 'Equipment',
    description: 'Higher-value equipment, machinery, and non-tool assets.',
  },
  {
    key: 'ppe',
    label: 'PPE / safety',
    description: 'Protective clothing, safety gear, and compliance wearables.',
  },
  {
    key: 'food',
    label: 'Food / refreshments',
    description: 'Meals, refreshments, and small crew catering purchases.',
    aliases: ['meals'],
  },
  {
    key: 'materials',
    label: 'Materials',
    description: 'Raw materials and job materials bought for fulfilment or site work.',
  },
  {
    key: 'tools',
    label: 'Tools',
    description: 'Hand tools, tool replacements, and workshop tooling.',
  },
  {
    key: 'consumables',
    label: 'Consumables',
    description: 'Adhesives, cleaners, fast-use items, and short-life stock.',
  },
  {
    key: 'travel',
    label: 'Travel',
    description: 'General travel costs that are not specifically fuel, toll, or parking.',
  },
  {
    key: 'utilities',
    label: 'Utilities',
    description: 'Electricity, water, internet, phone, and recurring utility charges.',
  },
  {
    key: 'professional',
    label: 'Professional services',
    description: 'Consulting, accounting, legal, and other professional fees.',
  },
  {
    key: 'software',
    label: 'Software / subscriptions',
    description: 'SaaS tools, licenses, subscriptions, and digital services.',
  },
  {
    key: 'casual_wages',
    label: 'Casual wages / salaries',
    description: 'Casual labour, day wages, temporary staff payments, and salary-related expenses.',
    aliases: ['casual_wage', 'wages', 'salary', 'salaries', 'labour'],
  },
  {
    key: 'other',
    label: 'Other',
    description: 'Catch-all default when nothing more specific applies.',
  },
];

const CATEGORY_EQUIVALENTS: Record<string, string[]> = {
  maintenance: ['servicing'],
  servicing: ['maintenance'],
  breakdown: ['roadside'],
  roadside: ['breakdown'],
  food: ['meals'],
  meals: ['food'],
  casual_wages: ['casual_wage', 'wages', 'salary', 'salaries', 'labour'],
  casual_wage: ['casual_wages', 'wages', 'salary', 'salaries', 'labour'],
  wages: ['casual_wages', 'casual_wage', 'salary', 'salaries', 'labour'],
  salary: ['casual_wages', 'casual_wage', 'wages', 'salaries', 'labour'],
};

const CATEGORY_KEYWORDS: Record<string, string[]> = {
  fuel: ['fuel', 'petrol', 'diesel', 'motor'],
  toll: ['toll', 'travel', 'motor'],
  parking: ['parking', 'travel', 'motor'],
  servicing: ['service', 'maintenance', 'repair', 'repairs', 'motor', 'vehicle'],
  maintenance: ['maintenance', 'repair', 'repairs', 'service', 'motor', 'vehicle'],
  roadside: ['roadside', 'breakdown', 'repair', 'repairs', 'motor', 'vehicle'],
  breakdown: ['breakdown', 'roadside', 'repair', 'repairs', 'motor', 'vehicle'],
  purchase: ['purchase', 'supplies', 'consumables', 'cost', 'expense'],
  parts: ['parts', 'spares', 'inventory', 'stock'],
  supplies: ['supplies', 'office', 'consumables'],
  equipment: ['equipment', 'tools', 'asset'],
  ppe: ['safety', 'protective', 'ppe'],
  food: ['meal', 'meals', 'refreshment', 'subsistence'],
  materials: ['materials', 'stock', 'consumables'],
  tools: ['tools', 'equipment'],
  consumables: ['consumables', 'supplies'],
  travel: ['travel', 'motor', 'vehicle'],
  meals: ['meal', 'meals', 'entertainment', 'subsistence'],
  utilities: ['utilities', 'electricity', 'water', 'telephone', 'internet'],
  professional: ['professional', 'consulting', 'legal', 'accounting'],
  software: ['software', 'subscription', 'saas', 'license'],
  casual_wages: ['wage', 'wages', 'salary', 'salaries', 'labour', 'casual', 'staff', 'pay'],
  casual_wage: ['wage', 'wages', 'salary', 'salaries', 'labour', 'casual', 'staff', 'pay'],
  wages: ['wage', 'wages', 'salary', 'salaries', 'labour', 'casual', 'staff', 'pay'],
  salary: ['salary', 'salaries', 'wage', 'wages', 'pay', 'remuneration'],
  other: ['expense', 'general'],
};

function normalizeToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function normalizeExpenseCategoryKey(value: string | null | undefined): string {
  return normalizeToken(String(value || ''));
}

export function normalizeExpenseAccountMappings(value: unknown): ExpenseAccountMappings {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const normalizedEntries = Object.entries(value as Record<string, unknown>)
    .map(([rawKey, rawCode]) => {
      const key = normalizeExpenseCategoryKey(rawKey);
      const code = typeof rawCode === 'string' ? rawCode.trim() : '';
      return key && code ? [key, code] as const : null;
    })
    .filter((entry): entry is readonly [string, string] => Boolean(entry));

  return Object.fromEntries(normalizedEntries);
}

export function getExpenseAccountByCode(
  accounts: ExpenseAccountOption[],
  accountCode?: string | null,
): ExpenseAccountOption | null {
  const normalizedCode = String(accountCode || '').trim().toLowerCase();
  if (!normalizedCode) {
    return null;
  }

  return accounts.find((account) => String(account.account_code || '').trim().toLowerCase() === normalizedCode) || null;
}

export function resolveExpenseAccountMappingCode(
  category: string | null | undefined,
  mappings: unknown,
  accounts: ExpenseAccountOption[] = [],
): string | null {
  const normalizedCategory = normalizeExpenseCategoryKey(category || 'other');
  if (!normalizedCategory) {
    return null;
  }

  const normalizedMappings = normalizeExpenseAccountMappings(mappings);
  const candidateKeys = [
    normalizedCategory,
    ...(CATEGORY_EQUIVALENTS[normalizedCategory] || []),
  ];

  for (const key of candidateKeys) {
    const mappedCode = normalizedMappings[key];
    if (!mappedCode) {
      continue;
    }

    if (accounts.length === 0) {
      return mappedCode;
    }

    const matchedAccount = getExpenseAccountByCode(accounts, mappedCode);
    if (matchedAccount) {
      return matchedAccount.account_code;
    }
  }

  return null;
}

export function suggestExpenseAccountCode(
  category: string | null | undefined,
  accounts: ExpenseAccountOption[],
): string | null {
  const normalizedCategory = normalizeToken(String(category || 'other'));
  const keywords = CATEGORY_KEYWORDS[normalizedCategory] || CATEGORY_KEYWORDS.other;

  let bestMatch: { code: string; score: number } | null = null;

  for (const account of accounts) {
    const haystack = `${account.account_code || ''} ${account.name || ''}`.toLowerCase();
    let score = 0;

    for (const keyword of keywords) {
      if (haystack.includes(keyword)) {
        score += haystack.includes(` ${keyword} `) ? 12 : 8;
      }
    }

    if (normalizedCategory !== 'other' && haystack.includes(normalizedCategory)) {
      score += 14;
    }

    if (score > 0 && (!bestMatch || score > bestMatch.score)) {
      bestMatch = { code: account.account_code, score };
    }
  }

  return bestMatch?.code || null;
}

/**
 * DIN line-item link requirements, modelled on QBO:
 *
 * - A line marked billable must resolve to a customer + job — identical to
 *   QBO's Billable flag requiring the Customer column. This is the ONLY thing
 *   that makes a customer/job link compulsory; a job-allocated line that is
 *   not billable may carry a link optionally or none at all.
 * - A stock allocation requires a linked inventory item only when the client
 *   has inventory movement tracking enabled — there is no movement to record
 *   when tracking is off, mirroring QBO where the item is optional for
 *   non-inventory-tracked items.
 * - Equipment (and vehicle) links are always optional — QBO has no
 *   equivalent dimension.
 */
export interface DinLineLinkCheckInput {
  allocation_type?: string | null;
  is_billable?: boolean | string | null;
  linked_customer_id?: string | null;
  linked_job_id?: string | null;
  linked_inventory_item_id?: string | null;
}

export interface DinLineLinkGaps {
  missingBillableCustomerJob: boolean;
  missingStockItem: boolean;
}

export function isLineItemBillable(isBillable: boolean | string | null | undefined): boolean {
  return isBillable === true || isBillable === 'true';
}

export function getDinLineLinkGaps(
  item: DinLineLinkCheckInput,
  opts: { inventoryTrackingEnabled?: boolean } = {},
): DinLineLinkGaps {
  const billable = isLineItemBillable(item.is_billable);
  return {
    missingBillableCustomerJob:
      billable && !(Boolean(item.linked_customer_id?.trim()) && Boolean(item.linked_job_id?.trim())),
    missingStockItem:
      item.allocation_type === 'stock' &&
      !item.linked_inventory_item_id?.trim() &&
      (opts.inventoryTrackingEnabled ?? true),
  };
}
